// filepath: apps/web/src/features/admin/templates/export-import/ReplaceTemplateDialog.tsx
//
// FR-UI-ADM-13 AC-4 (Q3) — "Replace existing template?" modal.
// Opens when the parsed-manifest `template_id` collides with an
// existing registry row.
//
// SCOPE DECISION (Q3, embedded in 01-requirement-validation.md
// `architectural_placement.scope_decisions[].id="Q3-replace-semantics"`):
//   AC-4's "Replace existing template?" prompt is a SPA-side
//   USER-INPUT gate ONLY. The backend enforces its own rules
//   (version immutability: same (template_id, version) with a
//   different content_hash returns 409 version_immutability_conflict;
//   with the same content_hash returns 200 replayed=true; a
//   different version returns 201 Created). No backend Replace
//   verb is invoked.
//
//   AC-5's FR-TPL-4 reference is tooltip-only on the Import
//   button — the install-time customization-preservation
//   semantics are an INSTALL-side concept, NOT an import-side
//   concept.
//
// RENDERED DIFF (text-only):
//   `existing v<X> → import v<Y>`
//
//   The body also includes:
//     (a) the existing row's `version` and `description`,
//     (b) the imported file's `version` and `description`,
//     (c) a one-line diff `existing v<X> → import v<Y>` rendered
//         as a text-only paragraph (NOT via `ConflictDiffPanel`
//         from FR-UI-ADM-12 — that panel renders INSTALLED vs
//         TARGET vendor-update shape, not registry-row vs
//         on-disk-bundle shape),
//     (d) a footer note "(server-side version immutability rules
//         apply on submit — this confirmation does not bypass
//         them)" making Q3's scoping explicit.
//
// FR-UI-CORE-9: the dialog mounts focusTrap on the container.

import React, { useEffect, useRef } from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { Dialog } from "@/design-system/Dialog";
import { focusTrap } from "@/runtime/focusTrap";
import type { TemplateRegistryRow } from "../registry/types";
import type { ImportPreview } from "./types";

interface ReplaceTemplateDialogProps {
  open: boolean;
  existing: TemplateRegistryRow | null;
  imported: ImportPreview | null;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ReplaceTemplateDialog({
  open,
  existing,
  imported,
  onConfirm,
  onCancel,
}: ReplaceTemplateDialogProps): React.ReactElement {
  const { t } = useTranslation();
  const dialogRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const container = dialogRef.current;
    if (!container) return;
    const teardown = focusTrap(container);
    return teardown;
  }, [open]);

  return (
    <Dialog
      open={open}
      title={t("admin.templates.export_import.dialog.replace.title")}
      onClose={onCancel}
      data-testid="template-bundle-replace-dialog"
    >
      <div ref={dialogRef} className="space-y-3 text-sm">
        {existing && imported ? (
          <>
            {/* (a) existing row */}
            <div
              className="rounded border border-border bg-background p-3 text-xs"
              data-testid="template-bundle-replace-existing"
            >
              <div className="font-medium">
                {t(
                  "admin.templates.export_import.dialog.replace.existing_label",
                )}
              </div>
              <div>
                <strong>{existing.template_id}</strong> v{existing.version}
              </div>
              <div className="text-muted-foreground">
                {existing.description}
              </div>
            </div>

            {/* (b) imported file */}
            <div
              className="rounded border border-border bg-background p-3 text-xs"
              data-testid="template-bundle-replace-imported"
            >
              <div className="font-medium">
                {t(
                  "admin.templates.export_import.dialog.replace.imported_label",
                )}
              </div>
              <div>
                <strong>{imported.template_id}</strong> v{imported.version}
              </div>
              <div className="text-muted-foreground">
                {imported.description}
              </div>
            </div>

            {/* (c) one-line text-only diff (NOT ConflictDiffPanel). */}
            <p
              className="rounded border border-border bg-muted px-3 py-2 text-xs font-mono"
              data-testid="template-bundle-replace-diff"
            >
              {t(
                "admin.templates.export_import.dialog.replace.diff",
                undefined,
                {
                  existing_version: existing.version,
                  import_version: imported.version,
                },
              )}
            </p>

            {/* (d) footer note — server-side version immutability rules */}
            <div
              className="rounded border border-border bg-background p-3 text-xs text-muted-foreground"
              data-testid="template-bundle-replace-server-rules-notice"
            >
              {t(
                "admin.templates.export_import.dialog.replace.server_rules_notice",
              )}
            </div>
          </>
        ) : null}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded border border-border bg-background px-3 py-2 text-xs font-medium hover:bg-muted"
            data-testid="template-bundle-replace-cancel"
          >
            {t("admin.templates.export_import.dialog.replace.cancel")}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="rounded bg-primary px-3 py-2 text-xs font-medium text-primary-foreground hover:bg-primary/90"
            data-testid="template-bundle-replace-confirm"
          >
            {t("admin.templates.export_import.dialog.replace.confirm")}
          </button>
        </div>
      </div>
    </Dialog>
  );
}
